/** @jsx jsx */
import React, { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch } from 'redux';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconTile } from '@atlaskit/icon';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import PageIcon from '@atlaskit/icon/core/page';
import LoadingSpinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import type { IssueType } from '@atlassian/jira-issue-shared-types/src/common/types/issue-hierarchy-type.tsx';
import SquareAddIconButton from '@atlassian/jira-issue-view-common-views/src/button/square-add-icon-button.tsx';
import { Container } from '@atlassian/jira-issue-view-common-views/src/smart-link-content/styled.tsx';
import { SectionHeadingTitle } from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { createConfluencePageLinkRequest } from '@atlassian/jira-issue-view-store/src/actions/confluence-pages-actions.tsx';
import {
	fireScreenAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { toHref } from '@atlassian/jira-shared-types/src/general.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useAdControlsFunctionality } from '../../../common/ad-controls/utils.tsx';
import AdControls from '../../../common/ad-controls/view.tsx';
import { USER_PROPERTY_DUMMY_LINK_CO_USE } from '../../../common/constants.tsx';
import { getConfluencePlaceholderTemplate } from '../../../common/utils.tsx';
import { useConfluenceDummyLinkContext } from '../../../controllers/confluence-dummy-link-context/index.tsx';
import { useDraftEpModal } from '../../../controllers/use-draft-embedded-page-modal-entry-point/use-draft-ep-modal.tsx';
import { createConfluenceDraft } from '../../../services/confluence/create-confluence-draft/index.tsx';
import { getActivePageUrl } from '../../../services/confluence/utils.tsx';
import messages from '../../messages.tsx';
import {
	CardWrapper,
	DummyGroupContainer,
	DummySectionHeading,
	LeftWrapper,
	RightWrapper,
	StyledTemplateName,
	TryLozenge,
} from '../../styled.tsx';
import { useIsCoUseEligible } from './co-use-eligibility.tsx';
import { createSpaceForProjectName } from './create-project-name-space/index.tsx';
import { TEST_IDS } from './index.tsx';

export type Props = {
	issueType?: IssueType;
	projectName?: string;
	shouldShowHeading: boolean;
	onAddButtonClicked: () => void;
	hasActivatedConfluenceWithinFourteenDays: boolean;
	onLinkPage?: (link: string, analyticsEvent: UIAnalyticsEvent) => void;
};

export const DummyConfluenceLinkCoUseComponent = ({
	issueType,
	onAddButtonClicked,
	projectName,
	shouldShowHeading,
	hasActivatedConfluenceWithinFourteenDays,
	onLinkPage,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const template = useMemo(() => getConfluencePlaceholderTemplate(issueType), [issueType]);
	const { showFlag } = useFlagsService();

	const { isDismissed, onDismissAd } = useAdControlsFunctionality({
		userPropertyPlaceholderTemplate: USER_PROPERTY_DUMMY_LINK_CO_USE, // change this
	});

	const { isCoUseEligible } = useIsCoUseEligible({
		template,
		isDismissed,
		hasActivatedConfluenceWithinFourteenDays,
	});

	const showDummyLink = !!template && isCoUseEligible;

	useEffect(() => {
		if (showDummyLink) {
			fireScreenAnalytics(createAnalyticsEvent({}));
		}
	}, [showDummyLink, createAnalyticsEvent]);

	const { cloudId } = useTenantContext();

	const accountId = useAccountId() || '';

	const onPublish = useCallback(async () => {
		try {
			await setUserProperties(accountId, USER_PROPERTY_DUMMY_LINK_CO_USE, JSON.stringify(true));
		} catch {
			fireErrorAnalytics({
				meta: {
					id: 'onPublishEmbeddedPagePlaceholderTemplateCoUse',
					packageName: 'jiraIssueViewBase',

					teamName: 'starburst',
				},
				error: new Error('Unable to update user property for placeholder template co use'),
			});
		}
	}, [accountId]);
	const epModalEntryPointContainer = useDraftEpModal({
		onPublish,
		...(fg('jira_issue_view_expand_modeless_ep') && { linkConfluencePage: onLinkPage }),
	});
	const { isEpModalOpen, setIsEpModalOpen, setActivePageUrl, isLoading, setIsLoading } =
		useConfluenceDummyLinkContext();

	const onClickPlaceholderTemplate = useCallback(async () => {
		fireUIAnalytics(
			createAnalyticsEvent({}),
			'button clicked',
			'confluencePlaceholderTemplateCoUse',
		);
		try {
			setIsLoading(true);
			const space = await createSpaceForProjectName(projectName || '');
			const spaceKey = space?.key;

			if (!spaceKey || !template) {
				return;
			}

			if (space?.spaceCreated) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'spaceWithProjectName created');
			}

			const result = await createConfluenceDraft(
				cloudId,
				spaceKey,
				template.blueprintModuleCompleteKey,
			);

			if (result) {
				const activePageUrl = getActivePageUrl(result.contentId, result.draftShareId, spaceKey);
				setActivePageUrl(activePageUrl);
				setIsEpModalOpen(true);
			}
		} catch {
			setIsLoading(false);
			showFlag({
				messageId:
					'issue-view-base.content.confluence-placeholder-template-experiment.confluence-dummy-link.experiments.placeholder-template-co-use.show-flag.error.page-load-fail',
				messageType: 'transactional',
				type: 'error',
				isAutoDismiss: true,
				title: formatMessage(messages.pageLoadFailFlagTitle),
				description: formatMessage(messages.pageLoadFailFlagDescription),
			});
			fireErrorAnalytics({
				meta: {
					id: 'onClickPlaceholderTemplateCoUse',
					packageName: 'jiraIssueViewBase',

					teamName: 'starburst',
				},
			});
		}
	}, [
		cloudId,
		projectName,
		setActivePageUrl,
		setIsEpModalOpen,
		template,
		formatMessage,
		showFlag,
		setIsLoading,
		createAnalyticsEvent,
	]);

	const onClickPlusButton = useCallback(async () => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'linkConfluencePage');
		onAddButtonClicked();
	}, [createAnalyticsEvent, onAddButtonClicked]);

	if (!showDummyLink) return null;

	return (
		<>
			{isEpModalOpen && epModalEntryPointContainer}
			{shouldShowHeading ? (
				<DummySectionHeading data-testid={TEST_IDS.header}>
					<DummySectionHeading>
						<SectionHeadingTitle>{formatMessage(messages.newHeading)}</SectionHeadingTitle>
					</DummySectionHeading>
					<DummySectionHeading>
						<AdControls menuButtonTestId={TEST_IDS.adMenuButton} onClickDismiss={onDismissAd} />
						<SquareAddIconButton label="link-a-confluence-page" onClick={onClickPlusButton} />
					</DummySectionHeading>
				</DummySectionHeading>
			) : null}
			<DummyGroupContainer data-testid={TEST_IDS.mainContainer}>
				<Container>
					<li css={listStyle}>
						<Button
							css={buttonStyle}
							appearance="subtle"
							shouldFitContainer
							spacing="none"
							onClick={onClickPlaceholderTemplate}
						>
							<CardWrapper>
								<LeftWrapper>
									{isVisualRefreshEnabled() ? (
										<IconTile
											icon={PageIcon}
											label=""
											appearance="blueBold"
											shape="square"
											size="16"
										/>
									) : (
										<Page16Icon label="" />
									)}
									<StyledTemplateName>{formatMessage(template.name)}</StyledTemplateName>
								</LeftWrapper>
								<RightWrapper>
									{isLoading ? (
										<>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<p css={LoadingTemplate}>{formatMessage(messages.loadingTemplate)}</p>
											<LoadingSpinner size="small" />
										</>
									) : (
										<>
											<TryLozenge appearance="new">
												{formatMessage(messages.tryTemplateLozenge)}
											</TryLozenge>
										</>
									)}
								</RightWrapper>
							</CardWrapper>
						</Button>
					</li>
				</Container>
			</DummyGroupContainer>
		</>
	);
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	onLinkPage: (link: string, analyticsEvent: UIAnalyticsEvent) =>
		dispatch(createConfluencePageLinkRequest(toHref(link), analyticsEvent)),
});

const mapStateToProps = () => ({});

const DummyConfluenceLinkCoUseWithReduxDispatchProps = connect(
	mapStateToProps,
	mapDispatchToProps,
)(DummyConfluenceLinkCoUseComponent);

export const DummyConfluenceLinkCoUse = componentWithCondition(
	() => fg('jira_issue_view_expand_modeless_ep'),
	DummyConfluenceLinkCoUseWithReduxDispatchProps,
	DummyConfluenceLinkCoUseComponent,
);

const buttonStyle = css({
	textDecoration: 'none',
	'&:focus, &:visited, &:link, &:active': {
		textDecoration: 'none',
	},
	'&:hover': {
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#000',
		transform: 'none',
	},
	height: 'auto',
});

const LoadingTemplate = css({
	color: token('color.text.subtlest'),
	font: token('font.body.UNSAFE_small'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const listStyle = css({
	listStyle: 'none',
});
